import { useContext, useEffect, useMemo, useState } from 'react';
import { AppContext } from '../contexts/AppContext';
import { fetchDMNativeArtifact } from '../services/DigitaltMuseum';

/**
 * Names of DigitaltMuseum objects per kiosk language, set up in the DM view (`settings.dmNameRules`).
 * DM has no translations, but museums can register names in other languages as e.g. alternative names
 * with the language as specification ("Gáppte", specification "Lulesamisk"). The specification is only in DM's
 * native object API, so each object is looked up once per session, and only when a rule applies to the current language.
 */

/** A rule from the CMS */
export interface DMNameRule {
  /** Kiosk language the rule applies to, or "all" */
  language?: string,
  /** DM name type to take the name from */
  source?: "name" | "preciseName" | "alternativeName",
  /** Accepted specifications, comma separated (e.g. "Lulesamisk,Julevsámegiella"). Empty: any name of the type */
  specification?: string,
  /** Field to replace. Only "title" for now */
  target?: "title",
  /** "replace" (default) shows only the name, "subtitle" shows the original title under it */
  display?: "replace" | "subtitle"
}

/** The title to show for an object */
export interface DMLocalizedName {
  title: string,
  /** The original title, when it is shown under the name (display "subtitle") */
  subtitle?: string
}

interface DMName {
  name: string,
  type: string,
  specification: string
}

/** An item with a DM UUID and its original title */
interface DMNamedItem {
  uniqueId: string,
  uuid?: string,
  title?: string
}

const NAME_TYPES = ["name", "preciseName", "alternativeName"];
const NORWEGIAN = ["no", "nb"];

/** Names per DM UUID, once looked up, so they can be read synchronously by cards, the popup and pages */
const namesByUuid: Record<string, DMName[]> = {};

const normalize = (value?: string) => (value || "").trim().toLowerCase();

/**
 * Look up an object's names in DM's native API (cached there). A failed lookup is tried again next time
 * @param {string} uuid DM UUID
 */
const loadDMNames = (uuid: string): Promise<void> => fetchDMNativeArtifact(uuid)
  .then(data => {
    namesByUuid[uuid] = (data?.names || [])
      .filter((name: any) => typeof name?.name === "string" && name.name.trim())
      .map((name: any) => ({ name: name.name.trim(), type: name.nameType || "", specification: name.specification || "" }));
  })
  .catch(() => undefined);

/**
 * The rules that apply to a language. Norwegian ("no" and "nb") count as the same language
 * @param {DMNameRule[]} rules `settings.dmNameRules` (from the CMS, so checked)
 * @param {string} language Current language
 * @returns {DMNameRule[]} Valid rules for the language, in the CMS order
 */
export const getActiveDMNameRules = (rules: DMNameRule[] | undefined, language?: string): DMNameRule[] => (Array.isArray(rules) ? rules : [])
  .filter((rule: DMNameRule) => rule && (rule.target || "title") === "title" && NAME_TYPES.includes(rule.source || "alternativeName"))
  .filter((rule: DMNameRule) => !rule.language || rule.language === "all" || rule.language === language
    || (NORWEGIAN.includes(rule.language) && NORWEGIAN.includes(language || "")));

/**
 * Find the name to show: the first rule (in order) that has a matching name wins, and the first matching name is used
 * @param {DMName[]} names The object's names
 * @param {DMNameRule[]} rules Rules for the current language
 * @param {string} originalTitle The object's title
 * @returns {DMLocalizedName|undefined} Undefined if no rule matches, then the original title is used
 */
const findDMLocalizedName = (names: DMName[], rules: DMNameRule[], originalTitle?: string): DMLocalizedName | undefined => {
  for (const rule of rules) {
    const specifications = (rule.specification || "").split(",").map(normalize).filter(Boolean);
    const match = names.find(name => name.type === (rule.source || "alternativeName")
      && (specifications.length === 0 || specifications.includes(normalize(name.specification))));
    if (match) {
      const showOriginal = rule.display === "subtitle" && originalTitle && normalize(originalTitle) !== normalize(match.name);
      return { title: match.name, subtitle: showOriginal ? originalTitle : undefined };
    }
  }
  return undefined;
}

/**
 * Titles for a list of DM objects (e.g. the cards), by unique ID. Objects without a matching name are left out.
 * Does nothing (no requests) when no rule applies to the current language
 * @param {DMNamedItem[]} items Objects with UUID and title
 * @param {DMNameRule[]} rules `settings.dmNameRules`
 * @returns {object} Titles by unique ID
 */
export const useDMLocalizedNames = (items: DMNamedItem[], rules: DMNameRule[] | undefined): Record<string, DMLocalizedName> => {
  const { currentLanguage } = useContext(AppContext);
  const activeRules = getActiveDMNameRules(rules, currentLanguage);
  const hasRules = activeRules.length > 0;
  // Re-render when looked up names arrive
  const [, setVersion] = useState(0);

  useEffect(() => {
    if (!hasRules) { return; }
    const missing = [...new Set(items.map(item => item.uuid).filter((uuid): uuid is string => Boolean(uuid) && !namesByUuid[uuid!]))];
    if (missing.length === 0) { return; }
    let isActive = true;
    Promise.all(missing.map(loadDMNames)).then(() => { isActive && setVersion(version => version + 1); });
    return () => { isActive = false; };
  }, [items, hasRules]);

  if (!hasRules) { return {}; }
  const result: Record<string, DMLocalizedName> = {};
  items.forEach(item => {
    const names = item.uuid ? namesByUuid[item.uuid] : undefined;
    const localized = names && findDMLocalizedName(names, activeRules, item.title);
    if (localized) { result[item.uniqueId] = localized; }
  });
  return result;
}

/**
 * Title for a single DM object (e.g. in the popup or as the heading of a folder)
 * @param {string} uuid DM UUID
 * @param {string} title The object's title
 * @param {DMNameRule[]} rules `settings.dmNameRules`
 * @returns {DMLocalizedName|undefined} Undefined if no rule matches (or while it is looked up)
 */
export const useDMLocalizedName = (uuid: string | undefined, title: string | undefined, rules: DMNameRule[] | undefined): DMLocalizedName | undefined => {
  const items = useMemo(() => uuid ? [{ uniqueId: uuid, uuid, title }] : [], [uuid, title]);
  return useDMLocalizedNames(items, rules)[uuid || ""];
}
